// © Microsoft Corporation. All rights reserved.

import { getFlight } from "@msnews/social-utils";
import { getSubscriptionsForPublisher, SubscriptionStatus } from "@msnews/subscription-sdk-wrapper";
import { getXPayToken, User } from "@msnews/social-data-service";
import { isUserSignedIn } from "@msnews/auth";
import { KeyValueArray, appEnvironment } from "@msnews/core";
import { OneServiceUtility, getServiceUrlForSubpath, sendRequest } from "@msnews/oneservice";

export async function hasPremiumSubscription (me: User, publisherId: string, fromXPay = false): Promise<boolean> {
    if (fromXPay) {
        return checkPremiumSubscriptionFromXPaySDK(me, publisherId);
    } else {
        return checkPremiumSubscriptionFromActionService(publisherId);
    }
}

/** Check if user has premium subscription for publisher with `publisherId` through XPay API */
async function checkPremiumSubscriptionFromXPaySDK (me: User, publisherId: string): Promise<boolean> {
    return getSubscriptionsForPublisher({
        publisherId: publisherId,
        authToken: await getXPayToken(me.id)
    }).then(response => {
        if (response.status && response.data && response.data.find(s => s.subscriptionType === "Premium")) {
            return true;
        }
        return false;
    }).catch(() => {
        return false;
    });
}

/** Check if user has premium subscription for publisher with `publisherId` through Action service */
async function checkPremiumSubscriptionFromActionService (publisherId: string): Promise<boolean> {
    const requestParams: RequestInit = {
        method: "GET",
        headers: await OneServiceUtility.getOneServiceHeaders()
    };
    requestParams.credentials = "include";
    const ocid = "anaheim-ntp";
    const serviceEndpoint = "news/users/me/actions";
    const serviceUrl = getServiceUrlForSubpath(serviceEndpoint);
    const commonParams: KeyValueArray<string, string> = isUserSignedIn() ?
        OneServiceUtility.getOneServiceParamsWithAuth(appEnvironment.UserId, ocid, false) :
        OneServiceUtility.getOneServiceParamsWithoutAuth(appEnvironment.UserId, ocid, false);
    const subscriptionParams = [
        {key: "actionType", value: "Subscribe"},
        {key: "targetType", value: "Provider"},
        {key: "market", value: "en-us"},
        {key: "publisherSubscriptionMockData", value: getFlight("prg-mock") ? "true" : null}
    ];
    [...commonParams, ...subscriptionParams].forEach((pair) => {
        if (pair.value) {
            serviceUrl.searchParams.set(pair.key, pair.value);
        }
    });

    let rawResponse: any;
    try {
        rawResponse = await (sendRequest(
            async () => {
                const innerResponse: Response = await fetch(serviceUrl.href, requestParams);
                if (innerResponse.status >= 400) {
                    const errMsg = `OneService returned error response. Status: ${innerResponse.status} for Subscribe Action}`;
                    throw new Error(errMsg);
                }
                return innerResponse;
            },
            "checkPremiumSubscriptionFromActionService"
        ));
    } catch (error) {
        return false;
    }
    const response = await rawResponse.json();
    return (response && response.value && response.value
        .filter(subscription => subscription?.metadata?.subscriptionType === "Premium" && subscription?.metadata?.subscriptionStatus === SubscriptionStatus.Active)
        .map(subscription => subscription.targetId)
        .includes(publisherId));
}